import React, { useContext } from 'react';
import { MdOutlineSearch } from 'react-icons/md';
import * as yup from 'yup';

import { DealerSearchContext } from '../../../../contexts/DealerSearchContext';

import FormContainer from '@repo/ui-components/form/FormContainer';
import { FormRow } from '@repo/ui-components/form/FormRow';
import BlockBuilder from '@repo/ui-components/sanity/BlockBuilder';

import { Label } from '../../../shadcn/Label';
import { Button } from '../../../shadcn/Button';
import { Input } from '../../../shadcn/Input';
import { Skeleton } from '../../../shadcn/Skeleton';

import {
  SearchFormProps,
  DealerContextProps,
} from './BlockBuilderDynamicDealerPricing';

function SearchForm({
  setLoading,
  loading,
  setErrors,
  errors,
  setZipCode,
  zipCode,
  setIsCompanyOwnedDealer,
  setActiveDealer,
  dealerSearchLabel,
  dealerSearchDescription,
  dealerUnavailableMessage,
  getInternalLink,
}: Readonly<SearchFormProps>) {
  const dealerSearchContext =
    useContext<DealerContextProps>(DealerSearchContext);
  const validationSchema = yup.object().shape({
    zipCode: yup
      .string()
      .required('ZIP/Postal Code is a required field.')
      .min(5, 'ZIP/Postal code must be at least 5 characters.'),
  });

  const handleSearch = async e => {
    e.preventDefault();

    setErrors({});

    let response;

    try {
      await validationSchema.validate({ zipCode }, { abortEarly: false });

      setLoading(true);

      response = await fetch(
        `${process.env.GATSBY_POSTCODE_SEARCH_API}?postalCode=${zipCode}`
      );
      const dealersResponse = await response?.json();

      // Trigger Dealer Search Flyout with results for more than 1 dealer
      if (dealersResponse?.length > 1) {
        dealerSearchContext?.setDealerSearchDisplayMode('compact');
        dealerSearchContext?.setDealerSearchActive(true);
        dealerSearchContext?.setDealerSearchResults(dealersResponse);
        setLoading(false);
      } else if (dealersResponse?.length === 1) {
        const companyOwnedDealer = dealersResponse?.find(
          dealer => dealer?.isCompanyOwned
        );
        setIsCompanyOwnedDealer(companyOwnedDealer?.isCompanyOwned || false);
        setActiveDealer(companyOwnedDealer || dealersResponse?.[0] || null);
        setLoading(false);
      } else {
        setErrors({
          zipCode:
            dealerUnavailableMessage?.text?.length > 0 ? (
              <div className="w-full text-base font-normal [&>p]:mb-0 text-foreground">
                <BlockBuilder
                  blocks={dealerUnavailableMessage.text}
                  getInternalLink={getInternalLink}
                />
              </div>
            ) : (
              `We're sorry, no dealers were found for the entered location. 
              Please try another zip code or contact customer support for assistance.`
            ),
        });
      }
    } catch (error) {
      if (error.inner) {
        const validationErrors = {};
        error.inner.forEach(err => {
          validationErrors[err.path] = err.message;
        });
        setErrors(validationErrors);
      } else {
        console.error(error);
      }
    }

    setLoading(false);
  };

  return (
    <FormContainer onSubmit={handleSearch} className="max-w-sm">
      <div className="flex flex-col gap-7">
        <div className="flex flex-col gap-2">
          <FormRow invalid={errors.zipCode}>
            <Label htmlFor="zipCode">
              {dealerSearchLabel ||
                'Enter your ZIP/Postal Code for pricing in your area:'}
              <div className="flex items-center mt-2">
                <Input
                  id="zipCode"
                  type="text"
                  value={zipCode}
                  onChange={e => setZipCode(e.target.value)}
                  className="flex-grow p-2 h-11 !rounded-r-none !mt-0"
                />
                <Button
                  className="w-11 h-11 p-2 !rounded-l-none bg-primary text-primary-foreground"
                  onClick={handleSearch}
                  aria-label="Search for pricing"
                >
                  <MdOutlineSearch className="w-6 h-6" aria-hidden="true" />
                </Button>
              </div>
              {errors?.zipCode && (
                <div>
                  <p className="my-2 text-base">{errors.zipCode}</p>
                </div>
              )}
            </Label>
          </FormRow>
          {dealerSearchDescription?.text?.length > 0 && (
            <div className="mt-5 w-full text-base font-normal [&>p]:mb-0 [&>p]:text-muted-foreground [&>p>a]:text-muted-foreground-link">
              <BlockBuilder
                blocks={dealerSearchDescription.text}
                getInternalLink={getInternalLink}
              />
            </div>
          )}
        </div>

        {loading && (
          <div className="flex flex-col gap-1">
            <Skeleton className="h-[14px] w-full" />
            <Skeleton className="h-[24px] w-full" />
          </div>
        )}
      </div>
    </FormContainer>
  );
}

export default SearchForm;
